import { ObjectTools, SolSelectValue } from '@sol/components';
import { Scope } from '@asm/components-platform-context/services/session.service';
import { AsmNavbarTools } from '@asm/components-platform-context/tools/asm-navbar-tools';

export class AsmTopbarState {
  private constructor(
    public readonly loading: boolean,
    public readonly partnerVisible: boolean,
    public readonly partner: string | undefined,
    public readonly partners: SolSelectValue[],
    public readonly scopeVisible: boolean,
    public readonly scope: string | undefined,
    public readonly scopes: SolSelectValue[],
    public readonly subscope: string | undefined,
    public readonly subscopes: SolSelectValue[],
    /** True when Angular is running inside the Vue host shell (embedded mode). */
    public readonly isEmbedded: boolean,
    /** Raw input: when true the caller wants to suppress the scope/partner selectors. */
    public readonly hideBreadcrumbs: boolean,
    /**
     * Derived: whether sol-topbar should activate custom breadcrumb content projection.
     * True when any of partnerVisible, scopeVisible (non-embedded), or hideBreadcrumbs is true.
     * Replaces the compound template expression to keep templates free of logic.
     */
    public readonly showCustomBreadcrumbs: boolean,
    /**
     * Derived: whether the scope/subscope selector should be shown.
     * True only when the platform app is active, not embedded, and not suppressed.
     */
    public readonly showScopeSwitcher: boolean,
    /** Derived: whether the partner selector should be shown. */
    public readonly showPartnerSwitcher: boolean,
  ) {}

  public static of(isEmbedded: boolean): AsmTopbarState {
    return new AsmTopbarState(
      true, // loading
      false, // partnerVisible
      undefined, // partner
      [], // partners
      false, // scopeVisible
      undefined, // scope
      [], // scopes
      undefined, // subscope
      [], // subscopes
      isEmbedded, // isEmbedded — environment fact, decided once
      false, // hideBreadcrumbs
      false, // showCustomBreadcrumbs
      false, // showScopeSwitcher
      false, // showPartnerSwitcher
    );
  }

  public withSessionData(
    loading: boolean,
    partner: string | undefined,
    partners: SolSelectValue[],
    scope: string | undefined,
    sessionScopes: Scope[],
    subscope: string | undefined,
    subscopes: SolSelectValue[],
  ): AsmTopbarState {
    const scopes = AsmNavbarTools.toScopeSelectValues(sessionScopes.filter(sessionScope => sessionScope.appVisible));
    if (
      this.loading === loading &&
      this.partner === partner &&
      ObjectTools.equals(this.partners, partners) &&
      this.scope === scope &&
      ObjectTools.equals(this.scopes, scopes) &&
      this.subscope === subscope &&
      ObjectTools.equals(this.subscopes, subscopes)
    ) {
      return this;
    }
    return this.copy({
      loading,
      partner,
      partners,
      scope,
      scopes,
      subscope,
      subscopes,
    });
  }

  public withNavigation(partnerVisible: boolean, scopeVisible: boolean): AsmTopbarState {
    if (this.partnerVisible === partnerVisible && this.scopeVisible === scopeVisible) {
      return this;
    }
    return this.copy({ partnerVisible, scopeVisible }).recalculateShowCustomBreadcrumbs();
  }

  public withHideBreadcrumbs(hideBreadcrumbs: boolean): AsmTopbarState {
    if (this.hideBreadcrumbs === hideBreadcrumbs) {
      return this;
    }
    return this.copy({ hideBreadcrumbs }).recalculateShowCustomBreadcrumbs();
  }

  private recalculateShowCustomBreadcrumbs(): AsmTopbarState {
    const showPartnerSwitcher = this.partnerVisible && !this.isEmbedded && !this.hideBreadcrumbs;
    const showScopeSwitcher = this.scopeVisible && !this.isEmbedded && !this.hideBreadcrumbs;
    const showCustomBreadcrumbs = showPartnerSwitcher || showScopeSwitcher || this.hideBreadcrumbs;
    return this.copy({ showCustomBreadcrumbs, showScopeSwitcher, showPartnerSwitcher });
  }

  private copy(props: Partial<AsmTopbarState>): AsmTopbarState {
    return ObjectTools.copy<AsmTopbarState>(this, props);
  }
}
